import {
  differenceInSeconds,
  parseISO,
} from "https://cdn.jsdelivr.net/npm/date-fns@3/+esm";

const BLOCK = 'time-countdown';

const UNIT_LABELS = Object.freeze({ days: 'Days', hours: 'Hours', minutes: 'Minutes' });

const SEL = Object.freeze({
  display:       `.${BLOCK}__display`,
  label:         `.${BLOCK}__label`,
  separatorDays: `.${BLOCK}__separator--days`,
  separatorHours:`.${BLOCK}__separator--hours`,
  expired:       `.${BLOCK}__expired`,
  digitValue:    `.${BLOCK}__digit-value`,
  typeDigit:     (type) => `.${BLOCK}__${type}-digit`,
  unitType:      (type) => `.${BLOCK}__unit--${type}`,
});

const CLS = Object.freeze({
  display:       `${BLOCK}__display`,
  label:         `${BLOCK}__label`,
  separator:     `${BLOCK}__separator`,
  separatorDays: `${BLOCK}__separator--days`,
  separatorHours:`${BLOCK}__separator--hours`,
  expired:       `${BLOCK}__expired`,
  digit:         `${BLOCK}__digit`,
  digitValue:    `${BLOCK}__digit-value`,
  unit:          `${BLOCK}__unit`,
  unitLabel:     `${BLOCK}__unit-label`,
  typeDigit:     (type) => `${BLOCK}__${type}-digit`,
  unitType:      (type) => `${BLOCK}__unit--${type}`,
});
 
class TimeCountdown extends HTMLElement {
  constructor() {
    super();
    this.interval = null;
  }
 
  connectedCallback() {
    const ends = this.getAttribute('ends');
 
    if (!ends) {
      console.warn('time-countdown missing "ends" attribute');
      return;
    }
 
    this.endDateTime = parseISO(ends);
 
    if (isNaN(this.endDateTime)) {
      console.warn('time-countdown received invalid "ends" value');
      return;
    }
 
    this.render();
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }
 
  disconnectedCallback() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }
 
  render() {
    const existingExpired = this.querySelector(SEL.expired);
 
    this.innerHTML = '';
 
    const label = document.createElement('div');
    label.className = CLS.label;
    label.textContent = this.getAttribute('label') || '';
 
    const display = document.createElement('div');
    display.className = CLS.display;
 
    const sepDays = document.createElement('span');
    sepDays.className = `${CLS.separator} ${CLS.separatorDays}`;
    sepDays.textContent = ':';
    display.appendChild(sepDays);

    const sepHours = document.createElement('span');
    sepHours.className = `${CLS.separator} ${CLS.separatorHours}`;
    sepHours.textContent = ':';
    display.appendChild(sepHours);
 
    this.appendChild(label);
    this.appendChild(display);
 
    if (existingExpired) {
      this.appendChild(existingExpired);
    } else {
      const expired = document.createElement('div');
      expired.className = CLS.expired;
      expired.style.display = 'none';
      expired.textContent = this.getAttribute('expired-message') || 'Expired';
      this.appendChild(expired);
    }
  }
 
  tick() {
    const totalSeconds = differenceInSeconds(this.endDateTime, new Date());
 
    if (totalSeconds <= 0) {
      this.showExpired();
      clearInterval(this.interval);
      this.interval = null;
      return;
    }
 
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;

    this.updateDigitDisplay(days.toString().padStart(2, '0'), 'days');
    this.updateDigitDisplay(hours.toString().padStart(2, '0'), 'hours');
    this.updateDigitDisplay(minutes.toString().padStart(2, '0'), 'minutes');
  }
 
  updateDigitDisplay(paddedValue, type) {
    const display = this.querySelector(SEL.display);
    if (!display) return;
 
    const existingDigits = display.querySelectorAll(SEL.typeDigit(type));
 
    if (existingDigits.length !== paddedValue.length) {
      this.rebuildDigitContainers(display, type, paddedValue.length);
    }
 
    const digits = display.querySelectorAll(SEL.typeDigit(type));
    for (let i = 0; i < paddedValue.length; i++) {
      const valueElement = digits[i]?.querySelector(SEL.digitValue);
      if (valueElement && valueElement.textContent !== paddedValue[i]) {
        valueElement.textContent = paddedValue[i];
      }
    }
  }
 
  rebuildDigitContainers(display, type, digitCount) {
    let unit = display.querySelector(SEL.unitType(type));

    if (!unit) {
      unit = document.createElement('div');
      unit.className = `${CLS.unit} ${CLS.unitType(type)}`;

      const unitLabel = document.createElement('span');
      unitLabel.className = CLS.unitLabel;
      unitLabel.textContent = this.getAttribute(`${type}-label`) || UNIT_LABELS[type];
      unit.appendChild(unitLabel);

      if (type === 'days') {
        display.insertBefore(unit, display.querySelector(SEL.separatorDays));
      } else if (type === 'hours') {
        display.insertBefore(unit, display.querySelector(SEL.separatorHours));
      } else {
        display.appendChild(unit);
      }
    }

    unit.querySelectorAll(SEL.typeDigit(type)).forEach(d => d.remove());

    const unitLabel = unit.querySelector(`.${CLS.unitLabel}`);
    for (let i = 0; i < digitCount; i++) {
      const digitDiv = document.createElement('div');
      digitDiv.className = `${CLS.digit} ${CLS.typeDigit(type)}`;

      const valueSpan = document.createElement('span');
      valueSpan.className = CLS.digitValue;
      valueSpan.textContent = '0';

      digitDiv.appendChild(valueSpan);
      unit.insertBefore(digitDiv, unitLabel);
    }
  }
 
  showExpired() {
    const display = this.querySelector(SEL.display);
    const label = this.querySelector(SEL.label);
    const expiredMessage = this.querySelector(SEL.expired);
 
    if (display) display.style.display = 'none';
    if (label) label.style.display = 'none';
    if (expiredMessage) expiredMessage.style.display = 'block';
  }
}

customElements.define("time-countdown-component", TimeCountdown);
